import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import {
  Lightbulb,
  Search,
  PenTool,
  HardHat,
  Users,
  PartyPopper,
  ChevronRight,
} from "lucide-react";

const stages = [
  {
    id: "idea",
    name: "Idea",
    icon: Lightbulb,
    description: "Define your vision",
    steps: [
      "Crystallize your concept & ideal guest profile",
      "Map what competitors charge and where they fall short",
      "Define the signature experience that makes you different",
    ],
  },
  {
    id: "feasibility",
    name: "Feasibility",
    icon: Search,
    description: "Validate the numbers",
    steps: [
      "Project room rates and occupancy for Years 1-3",
      "Calculate your return vs. what it costs to build",
      "Model worst-case scenarios to ensure you can cover debt",
    ],
  },
  {
    id: "design",
    name: "Design",
    icon: PenTool,
    description: "Design for margin",
    steps: [
      "Optimize room sizes to maximize revenue per square meter",
      "Plan efficient back-of-house to reduce staffing needs",
      "Program food & beverage spaces that actually make money",
    ],
  },
  {
    id: "build",
    name: "Build",
    icon: HardHat,
    description: "Control the build",
    steps: [
      "Lock contractor pricing before material costs spike",
      "Order furniture & equipment 9-12 months before opening",
      "Build contingency into every budget line",
    ],
  },
  {
    id: "preopening",
    name: "Pre-Opening",
    icon: Users,
    description: "Load the pipeline",
    steps: [
      "Recruit your General Manager 6+ months before opening",
      "Set up booking systems and online travel agency strategy",
      "Build service playbooks and train your founding team",
    ],
  },
  {
    id: "launch",
    name: "Launch",
    icon: PartyPopper,
    description: "Iterate fast",
    steps: [
      "Run a friends & family preview to stress-test operations",
      "Invite travel journalists and influencers for early coverage",
      "Monitor guest reviews daily and fix issues immediately",
    ],
  },
];

export const JourneyTimeline = () => {
  const [activeStage, setActiveStage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % stages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused]);

  const currentStage = stages[activeStage];
  const Icon = currentStage.icon;

  return (
    <Card
      className="p-6 bg-card/80 backdrop-blur-sm border-border/50"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="space-y-5">
        <div className="flex justify-between items-center gap-1">
          {stages.map((stage, index) => {
            const StageIcon = stage.icon;
            const isActive = index === activeStage;
            const isPast = index < activeStage;

            return (
              <button
                key={stage.id}
                onClick={() => setActiveStage(index)}
                className={`flex-1 py-2 px-1 rounded-lg transition-all duration-300 ${
                  isActive
                    ? "bg-[hsl(215_85%_45%)] text-white shadow-lg scale-105"
                    : isPast
                      ? "bg-[hsl(215_85%_96%)] text-[hsl(215_85%_45%)]"
                      : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
                }`}
              >
                <StageIcon className="w-4 h-4 mx-auto" />
              </button>
            );
          })}
        </div>

        <div className="h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full bg-[hsl(215_85%_45%)] transition-all duration-300 ease-out"
            style={{ width: `${((activeStage + 1) / stages.length) * 100}%` }}
          />
        </div>

        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[hsl(215_85%_96%)] mb-3 mx-auto">
            <Icon className="w-7 h-7 text-[hsl(215_85%_45%)]" />
          </div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
            Stage {activeStage + 1} of {stages.length}
          </p>
          <h3 className="text-2xl font-bold text-foreground mb-1">
            {currentStage.name}
          </h3>
          <p className="text-muted-foreground text-sm mb-4">
            {currentStage.description}
          </p>

          <div className="text-left space-y-2 mb-4">
            {currentStage.steps.map((step, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <ChevronRight className="w-4 h-4 text-[hsl(215_85%_45%)] mt-0.5 flex-shrink-0" />
                <span className="text-foreground">{step}</span>
              </div>
            ))}
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[hsl(215_85%_96%)] rounded-full">
            <span className="text-xs text-[hsl(215_85%_45%)] font-medium">
              + 100s more steps available in platform
            </span>
          </div>
        </div>

        <div className="flex justify-between text-xs text-muted-foreground">
          {stages.map((stage, index) => (
            <span
              key={stage.id}
              className={`transition-colors ${index === activeStage ? "text-[hsl(215_85%_45%)] font-medium" : ""}`}
            >
              {stage.name}
            </span>
          ))}
        </div>
      </div>
    </Card>
  );
};
